import ShelfPack from '@mapbox/shelf-pack';
import { EventEmitter } from 'events';

import { FilletTypes } from './fillet_utils';
import Part from './part';
import IdleState from './states/idle_state';

const minScale = 0.1;
const maxScale = 2.0;
const offset = 500;
const interval = 50;

class Operation {
  constructor (part, point, fromType, toType) {
    this.part = part;
    this.point = point;
    this.fromType = fromType;
    this.toType = toType;
  }

  redo () {
    this.part.force(this.point, this.toType);
  }

  undo () {
    this.part.force(this.point, this.fromType);
  }
}

class SequenceOperation {
  constructor (operations) {
    this.operations = operations;
  }

  redo () {
    this.operations.forEach(op => op.redo());
  }

  undo () {
    this.operations.forEach(op => op.undo());
  }
}

export default class Fillet extends EventEmitter {
  constructor (dom) {
    super();

    // ui
    this.state = new IdleState();
    this.events = [];
    this.position = new THREE.Vector2();
    this.scale = 1.0;
    this.start = new THREE.Vector2();
    this.prev = new THREE.Vector2();

    // dom
    this.root = dom;
    this.rect = this.root.getBoundingClientRect();

    this.view = this.createView();
    this.grid = this.createGrid();
    this.view.appendChild(this.grid);
    this.root.appendChild(this.view);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg = svg;
    this.svg.setAttribute('width', 6000);
    // this.svg.setAttribute('height', 6000);
    this.svg.setAttribute('height', 15000);
    this.view.appendChild(svg);

    this.type = FilletTypes.DogBone;
    this.parts = [];
    this.current = 0;
    this.history = [];

    this.setupEvent(dom);

    this.move(-offset, -offset);
  }

  load (pathJson) {
    this.clear();

    const paths = pathJson.paths;

    // 板厚が30mm以上の時は12.7mm
    const thickness = (pathJson.thickness !== undefined) ? Number(pathJson.thickness) : 24;
    const mill = (thickness < 30) ? 6.35 : 12.7;

    this.parts = paths.map(source => {
      return new Part(source, mill);
    });

    this.parts.forEach(part => {
      this.appendSVGElement(part.element);
      part.on('select', (_, point) => {
        if (point.selected) {
          this.selectMultiplePoints();
        } else {
          const prevType = point.type;
          if (prevType !== this.type) {
            part.select(point, this.type);
            const op = new Operation(part, point, prevType, point.type);
            this.pushHistory(op);
          }
        }
      });
    });

    this.nest();
  }

  selectMultiplePoints (all = false) {
    const operations = this.parts.map(part => {
      const candidates = part.getAllCandidates();
      const points = candidates.filter(p => all || p.selected);
      return points.filter(p => p.type !== this.type).map(point => {
        const prevType = point.type;
        part.select(point, this.type);
        return new Operation(part, point, prevType, point.type);
      });
    }).flat(1);
    if (operations.length > 0) {
      const op = new SequenceOperation(operations);
      this.pushHistory(op);
    }
  }

  clear () {
    this.parts.forEach(part => {
      this.svg.removeChild(part.element);
    });
    this.parts = [];
    this.current = 0;
    this.history = [];
  }

  build () {
    return this.parts.map(part => part.build());
  }

  serialize () {
    return this.parts.map(part => part.serialize());
  }

  deserialize (source = []) {
    if (source.length !== this.parts.length) return;

    this.parts.forEach((part, idx) => {
      const src = source[idx];
      part.deserialize(src);
    });
  }

  nest () {
    const partClearance = 10;
    const binClearance = 10;
    const clearance = partClearance + binClearance;

    const boxes = this.parts.map((p, idx) => {
      const bb = p.bounds();
      const w = bb.width;
      return {
        id: idx,
        width: w + clearance + partClearance,
        height: bb.height + clearance + partClearance,
        offset: {
          x: bb.x - clearance,
          y: bb.y - clearance
        }
      };
    });

    const sources = boxes.slice();
    const results = [];
    const width = 6000;
    const height = 1000;

    // @CAUTION: invert xy size due to resize direction in ShelfPack
    let sprite = new ShelfPack(height, width, { autoResize: true });

    let index = 0;
    while (sources.length > 0) {
      let latest = sources[0];

      let result = sprite.packOne(latest.height, latest.width, latest.id);
      if (result) {
        sources.splice(0, 1);
        results.push({
          index: index,
          bin: result
        });
      } else {
        if (sprite.bins.length <= 0) {
          break;
        }
        // create new shelf
        index++;
        sprite = new ShelfPack(height, width, { autoResize: true });
      }
    }

    const ox = offset;
    const oy = offset;

    results.forEach(result => {
      const bin = result.bin;
      const part = this.parts[bin.id];
      const box = boxes[bin.id];
      const dx = (width + interval) * result.index;
      // @CAUTION: invert xy position for bin
      part.moveTo(bin.y - box.offset.x + ox + dx, bin.x - box.offset.y + oy);
    });

    // parts that doesn't fit in the bin
    sources.forEach(box => {
      const part = this.parts[box.id];
      part.moveTo(-box.offset.x + ox, -box.offset.y + oy);
    });

    const maxX = this.parts.reduce((pre, cur) => {
      const { x, width: w } = cur.bounds();
      return Math.max(pre, Math.abs(cur.position.x) + x + w);
    }, 0);

    this.svg.setAttribute('width', maxX + offset + clearance);
  }

  select (type) {
    this.type = type;
  }

  setupEvent (el) {
    this.addEvent(el, 'contextmenu', (e) => {
      e.preventDefault();
    });

    this.addEvent(el, 'wheel', (e) => {
      let r = this.root.getBoundingClientRect();
      let { clientX, clientY } = e;
      const x = clientX - r.x;
      const y = clientY - r.y;
      let delta = -e.deltaY * 0.001;
      this.zoom(delta, x, y, r.width, r.height);
      e.preventDefault();
    });

    this.addEvent(el, 'mousedown', this.onMouseDown.bind(this), false);
    this.addEvent(el, 'mousemove', this.onMouseMove.bind(this), false);
    this.addEvent(el, 'mouseup', this.onMouseUp.bind(this), false);
    this.addEvent(window, 'keydown', this.onKeyDown.bind(this), false);
    this.addEvent(window, 'keyup', this.onKeyUp.bind(this), false);
  }

  appendElement (dom) {
    this.root.appendChild(dom);
    this.move(0, 0); // to fix a bug in Chrome
  }

  appendSVGElement (element) {
    this.svg.appendChild(element);
    this.move(0, 0); // to fix a bug in Chrome
  }

  createView () {
    let view = document.createElement('div');
    view.classList.add('view');
    view.style.transformOrigin = '0px 0px 0px';
    // view.style.pointerEvents = 'none';
    return view;
  }

  createGrid () {
    let grid = document.createElement('div');
    grid.classList.add('grid');
    return grid;
  }

  convert (domX, domY) {
    return {
      x: (domX - this.position.x) / this.scale,
      y: (domY - this.position.y) / this.scale
    };
  }

  pan (dx, dy) {
    this.move(dx, dy);
  }

  panable (button, shiftKey) {
    return (button === 0 && shiftKey) || button === 1 || button === 2;
  }

  setIsPanning (panning) {
    if (panning) {
      this.root.classList.add('panning');
    } else {
      this.root.classList.remove('panning');
    }
  }

  move (dx, dy) {
    this.moveTo(this.position.x + dx, this.position.y + dy);
    this.transform(this.position.x, this.position.y, this.scale);
  }

  moveTo (x, y) {
    this.position.x = x;
    this.position.y = y;
    this.transform(this.position.x, this.position.y, this.scale);
  }

  zoom (delta, x, y, w, h) {
    let ns = this.scale * (1 + delta);
    if (ns < minScale || maxScale < ns) return;

    // calculate a absolute position
    let ax = this.position.x / this.scale;
    let ay = this.position.y / this.scale;

    let rx = x / w;
    let ry = y / h;

    this.position.x = (ax * ns) - w * delta * rx;
    this.position.y = (ay * ns) - h * delta * ry;
    this.scale = ns;

    this.transform(this.position.x, this.position.y, this.scale);
  }

  transform (px, py, s) {
    this.view.style.transform = `translate(${px}px, ${py}px) scale(${s})`;

    const x = px / s;
    const y = py / s;
    this.grid.style.transform = `translate(${-x}px, ${-y}px)`;

    let percent = `${100 / s}%`;
    this.grid.style.width = this.grid.style.height = percent;
    this.grid.style.backgroundPosition = `${x}px ${y}px`;
    this.grid.style.opacity = Math.min(1, s * s); ;
  }

  onMouseDown (e) {
    this.state = this.state.mouseDown(this, e);
  }

  onMouseMove (e) {
    this.state = this.state.mouseMove(this, e);
  }

  onMouseUp (e) {
    this.state = this.state.mouseUp(this, e);
  }

  onKeyDown (e) {
    if (e.target !== document.body) return;
    e.preventDefault();

    if (e.ctrlKey || e.metaKey) {
      const key = e.key;
      if (key === 'y' || key === 'Z') {
        this.redo();
      } else if (key === 'z') {
        this.undo();
      }
    }
  }

  onKeyUp (e) {
    e.preventDefault();
  }

  addEvent (el, trigger, callback) {
    const evt = {
      el: el,
      trigger: trigger,
      callback: callback
    };
    this.events.push(evt);
    el.addEventListener(trigger, callback, false);
  }

  all () {
    this.selectMultiplePoints(true);
  }

  redo () {
    if (this.current >= this.history.length) return;
    this.history[this.current].redo();
    this.current++;
  }

  undo () {
    if (this.current <= 0) return;
    this.current--;
    this.history[this.current].undo();
  }

  pushHistory (op) {
    let len = this.history.length;
    if (this.current < len) {
      this.history.splice(this.current, len - this.current);
    }
    this.history.push(op);
    this.current++;

    const limit = 15;
    if (this.history.length > limit) {
      this.history.splice(0, 1);
      this.current--;
    }

    return op;
  }

  destroy () {
    this.events.forEach(evt => {
      evt.el.removeEventListener(evt.trigger, evt.callback);
    });
    this.events = [];
  }

  updateMill (mill) {
    this.parts.forEach(part => {
      part.updateMill(mill);
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/fillet/fillet.js